/* ==========================================================================
   rn-photo.js — Renegade photography behaviour

   Two jobs, no dependencies, ~4KB:

   1. REVEAL. Elements marked data-rn-reveal fade up a few pixels the first
      time they enter the viewport. Nothing is hidden until this script has
      run (the hiding hangs off .rn-js, added below), so a slow or failed
      script never leaves photographs invisible. Skipped entirely for people
      who ask for reduced motion.

   2. LIGHTBOX. Any [data-rn-lb] button opens a full screen viewer built on
      the native <dialog> element, which gives focus trapping and Escape to
      close for free. Photographs sharing a data-rn-lb value page together:
      arrow keys, on-screen buttons, or a horizontal swipe on touch. Focus
      returns to the photograph that opened it. Hidden duplicates (the
      desktop/mobile variants in Make it yours) are skipped when paging.

   3. FULL STOPS. A full stop that ends a sentence in a heading (h1–h3 and
      the hero display line) is wrapped in <span class="rn-stop"> so it can
      be set in Utopia yellow. Done here rather than in each section so the
      shared lp-cta snippet, used by other landing pages, is not touched.
      The text itself is unchanged for search engines and screen readers.

   A button with data-rn-lb-start="<group>" opens that group's viewer at its
   first photograph (the View all button in Past commissions).

   Scoped to .lp--renegade; loaded only by page.RenegadeLanding.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('.lp--renegade');
  if (!root) return;
  root.classList.add('rn-js');

  /* ---- 1. reveal ------------------------------------------------------- */

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var reveals = root.querySelectorAll('[data-rn-reveal]');

  if (!reduce && 'IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    Array.prototype.forEach.call(reveals, function (el) { io.observe(el); });
  } else {
    Array.prototype.forEach.call(reveals, function (el) { el.classList.add('is-in'); });
  }

  /* ---- 2. lightbox ----------------------------------------------------- */

  var dlg, img, cap, count, prevB, nextB;
  var items = [];
  var idx = 0;
  var opener = null;
  var startX = null;

  function visible(el) { return el.offsetParent !== null; }

  function show(i) {
    idx = (i + items.length) % items.length;
    var b = items[idx];
    img.src = b.getAttribute('data-rn-lb-src');
    img.alt = b.getAttribute('data-rn-lb-alt') || '';
    cap.textContent = b.getAttribute('data-rn-lb-caption') || '';
    var many = items.length > 1;
    count.textContent = many ? (idx + 1) + ' / ' + items.length : '';
    prevB.hidden = !many;
    nextB.hidden = !many;
  }

  function close() {
    if (dlg.close && dlg.open) { dlg.close(); } else { dlg.removeAttribute('open'); onClosed(); }
  }

  function onClosed() {
    document.documentElement.classList.remove('rn-lb-open');
    img.removeAttribute('src');
    if (opener) { opener.focus(); opener = null; }
  }

  function build() {
    dlg = document.createElement('dialog');
    dlg.className = 'rn-lb';
    dlg.setAttribute('aria-label', 'Photograph viewer');
    dlg.innerHTML =
      '<div class="rn-lb__stage"><img class="rn-lb__img" alt="" decoding="async"></div>' +
      '<div class="rn-lb__bar"><p class="rn-lb__cap"></p><span class="rn-lb__count" aria-live="polite"></span></div>' +
      '<button type="button" class="rn-lb__btn rn-lb__close" aria-label="Close">&times;</button>' +
      '<button type="button" class="rn-lb__btn rn-lb__prev" aria-label="Previous photograph">&lsaquo;</button>' +
      '<button type="button" class="rn-lb__btn rn-lb__next" aria-label="Next photograph">&rsaquo;</button>';
    root.appendChild(dlg);

    img = dlg.querySelector('.rn-lb__img');
    cap = dlg.querySelector('.rn-lb__cap');
    count = dlg.querySelector('.rn-lb__count');
    prevB = dlg.querySelector('.rn-lb__prev');
    nextB = dlg.querySelector('.rn-lb__next');

    dlg.querySelector('.rn-lb__close').addEventListener('click', close);
    prevB.addEventListener('click', function () { show(idx - 1); });
    nextB.addEventListener('click', function () { show(idx + 1); });

    /* A click on the dark surround closes; a click on the photograph does not. */
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg || e.target.classList.contains('rn-lb__stage')) close();
    });

    dlg.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight' && items.length > 1) { e.preventDefault(); show(idx + 1); }
      else if (e.key === 'ArrowLeft' && items.length > 1) { e.preventDefault(); show(idx - 1); }
      else if (e.key === 'Escape' && !dlg.showModal) { close(); }
    });

    dlg.addEventListener('close', onClosed);

    var stage = dlg.querySelector('.rn-lb__stage');
    stage.addEventListener('touchstart', function (e) {
      startX = e.touches.length === 1 ? e.touches[0].clientX : null;
    }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (startX === null || items.length < 2) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 48) show(idx + (dx < 0 ? 1 : -1));
      startX = null;
    }, { passive: true });
  }

  root.addEventListener('click', function (e) {
    var start = e.target.closest ? e.target.closest('[data-rn-lb-start]') : null;
    if (start && root.contains(start)) {
      var g = start.getAttribute('data-rn-lb-start');
      var first = Array.prototype.filter.call(root.querySelectorAll('[data-rn-lb]'), function (el) {
        return el.getAttribute('data-rn-lb') === g && visible(el);
      })[0];
      if (first) first.click();
      return;
    }

    var b = e.target.closest ? e.target.closest('[data-rn-lb]') : null;
    if (!b || !root.contains(b)) return;
    e.preventDefault();

    var group = b.getAttribute('data-rn-lb');
    items = Array.prototype.filter.call(root.querySelectorAll('[data-rn-lb]'), function (el) {
      return el.getAttribute('data-rn-lb') === group && visible(el);
    });
    if (!items.length) items = [b];

    if (!dlg) build();
    opener = b;
    show(Math.max(0, items.indexOf(b)));

    document.documentElement.classList.add('rn-lb-open');
    if (dlg.showModal) { dlg.showModal(); } else { dlg.setAttribute('open', ''); }
    dlg.querySelector('.rn-lb__close').focus();
  });

  /* ---- 3. full stops --------------------------------------------------- */

  function stops(scope) {
    var heads = scope.querySelectorAll('h1, h2, h3, .rn-display');
    Array.prototype.forEach.call(heads, function (h) {
      if (h.hasAttribute('data-rn-stops')) return;
      h.setAttribute('data-rn-stops', '');
      var walker = document.createTreeWalker(h, NodeFilter.SHOW_TEXT, null, false);
      var nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(function (t) {
        var s = t.nodeValue;
        /* A full stop at the end of a sentence: not part of a number (4.6),
           not an ellipsis, followed by a space or the end of the text. */
        var re = /(^|[^\d.])\.(?=\s|$)/g;
        if (!re.test(s)) return;
        re.lastIndex = 0;
        var frag = document.createDocumentFragment();
        var last = 0, m;
        while ((m = re.exec(s)) !== null) {
          var dot = m.index + m[1].length;
          frag.appendChild(document.createTextNode(s.slice(last, dot)));
          var sp = document.createElement('span');
          sp.className = 'rn-stop';
          sp.textContent = '.';
          frag.appendChild(sp);
          last = dot + 1;
        }
        frag.appendChild(document.createTextNode(s.slice(last)));
        t.parentNode.replaceChild(frag, t);
      });
    });
  }

  stops(root);

  /* Theme Editor re-renders a section after each change; catch it up, and
     show its photographs straight away rather than waiting for a reveal. */
  document.addEventListener('shopify:section:load', function (e) {
    if (!root.contains(e.target)) return;
    stops(e.target);
    Array.prototype.forEach.call(e.target.querySelectorAll('[data-rn-reveal]'), function (el) {
      el.classList.add('is-in');
    });
  });
})();
